import { useFormikContext } from 'formik';
import parse, { attributesToProps, domToReact } from 'html-react-parser';
import { isUndefined, mapValues, isObject, isArray, isFunction, has } from 'lodash';
import { stringify } from 'qs';
import React, { useContext, useEffect } from 'react';
import { IntlProvider } from 'react-intl';

var __defProp$1 = Object.defineProperty;
var __getOwnPropSymbols$1 = Object.getOwnPropertySymbols;
var __hasOwnProp$1 = Object.prototype.hasOwnProperty;
var __propIsEnum$1 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$1 = (obj, key, value) => key in obj ? __defProp$1(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$1 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$1.call(b, prop))
      __defNormalProp$1(a, prop, b[prop]);
  if (__getOwnPropSymbols$1)
    for (var prop of __getOwnPropSymbols$1(b)) {
      if (__propIsEnum$1.call(b, prop))
        __defNormalProp$1(a, prop, b[prop]);
    }
  return a;
};
const OrganismStatusContext = React.createContext(200);
const useOrganismStatus = () => useContext(OrganismStatusContext);
const OrganismStatusProvider = ({
  children,
  status
}) => /* @__PURE__ */ React.createElement(OrganismStatusContext.Provider, {
  value: status
}, children);
function createMapper(mappers, context) {
  return function(input, contextOverride) {
    const filtered = input.filter((item) => {
      if (isUndefined(item)) {
        return false;
      }
      if (!mappers[item.__typename]) {
        console.error(`No mapper defined for ${item.__typename}`);
        return false;
      }
      return true;
    });
    return filtered.map((item, index) => {
      return mappers[item.__typename](item, {
        items: filtered,
        payload: contextOverride || context,
        index
      });
    });
  };
}
function isOrganismAsyncInput(input) {
  return isFunction(input);
}
function toHook(input) {
  return isOrganismAsyncInput(input) ? input : () => [input, 200];
}
function route(Component, layoutMap) {
  return [Component, layoutMap];
}
function GroupLayout(props) {
  return /* @__PURE__ */ React.createElement(React.Fragment, null, props.items);
}
function group(layoutMap) {
  return [GroupLayout, layoutMap];
}
function withOrganismProps(Component) {
  return function OrganismHost(input, index) {
    const [data, status] = toHook(input)();
    return /* @__PURE__ */ React.createElement(OrganismStatusProvider, {
      status,
      key: index
    }, /* @__PURE__ */ React.createElement(Component, __spreadValues$1({}, data)));
  };
}
function isOrganismMap(input) {
  return isObject(input);
}
function Route({
  definition,
  input,
  intl,
  children
}) {
  return /* @__PURE__ */ React.createElement(IntlProvider, __spreadValues$1({}, intl), React.createElement(definition[0], mapValues(definition[1], (entry, key) => {
    const organismProps = input[key];
    if (isOrganismMap(entry) && isArray(organismProps)) {
      return organismProps.map((organism, index) => {
        if (!entry[organism.key]) {
          console.warn(`The "${organism.key}" key is missing in the "${key}" of a route. Debug data follows.`, {
            organismKey: organism.key,
            key,
            routeDefinition: definition,
            input
          });
          return React.createElement("div");
        }
        return withOrganismProps(entry[organism.key])(organism.input, index);
      });
    } else if (isFunction(entry)) {
      return withOrganismProps(entry)(input[key], 0);
    }
  }), children));
}
function Group({
  definition,
  input
}) {
  return React.createElement(definition[0], mapValues(definition[1], (entry, key) => {
    const organismProps = input[key];
    if (isOrganismMap(entry) && isArray(organismProps)) {
      return organismProps.map((organism, index) => {
        if (!entry[organism.key]) {
          console.warn(`The "${organism.key}" key is missing in the "${key}" of a group. Debug data follows.`, {
            organismKey: organism.key,
            key,
            groupDefinition: definition,
            input
          });
          return React.createElement("div");
        }
        return withOrganismProps(entry[organism.key])(organism.input, index);
      });
    } else if (isFunction(entry)) {
      return withOrganismProps(entry)(input[key], 0);
    }
  }));
}

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
const isInternalTarget = (target) => typeof target === "undefined" || target === "" || target === "_self";
const isRelative = (url) => (url == null ? void 0 : url.startsWith("#")) || (url == null ? void 0 : url.startsWith("?")) || Boolean(url == null ? void 0 : url.match(/^\/(?!\/)/));
function hasChildren(input) {
  return has(input, "children");
}
function isText(input) {
  return has(input, "data");
}
const slugify = (...args) => {
  const value = args.join(" ");
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim().replace(/[^a-z0-9 ]/g, "").replace(/\s+/g, "-");
};
function FormikChanges({
  onChange
}) {
  const { values } = useFormikContext();
  useEffect(() => {
    onChange(values);
  }, [values, onChange]);
  return null;
}
function FormikInitialValues({
  useInitialValues
}) {
  const { touched, setFieldTouched, setFieldValue } = useFormikContext();
  const values = useInitialValues();
  useEffect(() => {
    if (values) {
      for (const field in values) {
        if (!touched[field]) {
          setFieldValue(field, values[field], false);
          setFieldTouched(field, true, false);
        }
      }
    }
  }, [touched, setFieldTouched, setFieldValue, values]);
  return null;
}
const isElement = (node) => Array.isArray(node.children);
function nodeToText(input) {
  return input.map((node) => {
    if (isText(node)) {
      return node.data.trim();
    }
    if (hasChildren(node)) {
      return nodeToText(node.children);
    }
    return "";
  }).join(" ");
}
const htmlParserOptions = (buildLink, classNames) => ({
  replace: (node) => {
    if (isElement(node)) {
      const { attribs, children, tagName } = node;
      const props = attributesToProps(attribs);
      const className = classNames == null ? void 0 : classNames[node.name];
      const addedClass = typeof className === "string" ? className : typeof className === "function" ? className(node) : void 0;
      if (addedClass) {
        props["className"] = [props["className"], addedClass].filter((c) => !!c).join(" ");
      }
      switch (tagName) {
        case "li": {
          const text = domToReact(children, htmlParserOptions(buildLink, classNames));
          return /* @__PURE__ */ React.createElement("li", __spreadValues({}, props), /* @__PURE__ */ React.createElement("div", null, text));
        }
        case "h2": {
          const text = domToReact(children, htmlParserOptions(buildLink, classNames));
          return /* @__PURE__ */ React.createElement("h2", __spreadProps(__spreadValues({}, props), {
            id: props.id || slugify(nodeToText(children))
          }), text);
        }
        case "a": {
          const Link = buildLink(props);
          return /* @__PURE__ */ React.createElement(Link, __spreadValues({}, props), domToReact(children, htmlParserOptions(buildLink, classNames)));
        }
        default: {
          return addedClass ? React.createElement(node.name, props, domToReact(children, htmlParserOptions(buildLink, classNames))) : void 0;
        }
      }
    }
  }
});
const buildHtmlBuilder = (buildLink) => (input) => {
  const Element2 = function MockHtml({ classNames }) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, parse(input, htmlParserOptions(buildLink, classNames)));
  };
  Element2.initialHtmlString = input;
  return Element2;
};
const isTruthy = (i) => Boolean(i);
const stripSlashes = (segment, index) => {
  if (index === 0) {
    return segment === "/" ? segment : segment.replace(/^\/{2,}/g, "/").replace(/\/+$/g, "");
  }
  return segment.replace(/^\/+|\/+$/g, "");
};
const buildUrl = (segments, query, queryOptions, fragment) => {
  const url = segments ? segments.filter(isTruthy).map(stripSlashes).join("/").replace(/^\/+/, "/") : "";
  const queryString = stringify(query, __spreadValues({
    skipNulls: true
  }, queryOptions));
  return [
    [url || "", queryString === "" ? null : queryString].filter((i) => typeof i === "string").join("?"),
    fragment
  ].filter(isTruthy).join("#");
};
const buildUrlBuilder = (segments, query, queryOptions, fragment) => (queryOverride, fragmentOverride) => buildUrl(segments, __spreadValues(__spreadValues({}, query || {}), queryOverride || {}), queryOptions, fragmentOverride || fragment);

export { FormikChanges, FormikInitialValues, Group, OrganismStatusProvider, Route, buildHtmlBuilder, buildUrl, buildUrlBuilder, createMapper, group, htmlParserOptions, isElement, isInternalTarget, isRelative, route, slugify, useOrganismStatus };
